import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ArrowPathIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function formatUsd(value) {
  const amount = Number(value);
  const safe = Number.isFinite(amount) ? amount : 0;
  return safe.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

function formatCount(value) {
  return Number(value || 0).toLocaleString('en-US');
}

function MoneyRow({ label, value, hint }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5">
      <div>
        <div className="text-sm text-gray-700">{label}</div>
        {hint ? <div className="text-xs text-gray-500">{hint}</div> : null}
      </div>
      <div className="text-sm font-medium text-gray-900 tabular-nums">{value}</div>
    </div>
  );
}

function Group({ title, usd, children }) {
  return (
    <section className="rounded-lg border border-gray-200 p-3">
      <div className="flex items-baseline justify-between gap-4">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        <div className="text-sm font-semibold text-gray-900 tabular-nums">{formatUsd(usd)}</div>
      </div>
      <div className="mt-1">{children}</div>
    </section>
  );
}

const RATE_LABELS = {
  il_mobile: 'IL mobile',
  il_local: 'IL local',
  us: 'US',
  other: 'Other',
};

/**
 * Internal list-rate provider cost for the account on screen.
 * Loads on its own so a slow usage query leaves the rest of the card usable.
 */
const BundleCostEstimate = ({ accountId }) => {
  const [month, setMonth] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setLoading(true);
    setError('');
    try {
      const params = month ? { month } : undefined;
      const res = await axios.get(
        `${API_BASE_URL}/api/admin/bundles/${accountId}/cost-estimates`,
        { params }
      );
      if (requestId !== requestRef.current) return;
      setData(res.data);
    } catch (err) {
      if (requestId !== requestRef.current) return;
      console.error('Cost estimate failed:', err);
      setData(null);
      setError('Could not load the cost estimate.');
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [accountId, month]);

  useEffect(() => {
    load();
  }, [load]);

  const period = data?.period;
  const allTime = month === 'all';

  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Estimated provider cost</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            List-rate estimate in USD for this account.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="All time"
            aria-pressed={allTime}
            onClick={() => setMonth('all')}
            className={`px-3 py-1.5 rounded-lg border text-sm font-medium ${
              allTime
                ? 'border-gray-900 bg-gray-900 text-white'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            All time
          </button>
          <div className="inline-flex items-center rounded-lg border border-gray-300 bg-white">
            <button
              type="button"
              aria-label="Previous month"
              disabled={loading || !period?.previousMonth}
              onClick={() => period?.previousMonth && setMonth(period.previousMonth)}
              className="p-1.5 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            <span className="px-2 text-sm font-medium text-gray-900 min-w-[8.5rem] text-center">
              {allTime ? 'All time' : (period?.label || '…')}
            </span>
            <button
              type="button"
              aria-label="Next month"
              disabled={loading || !period?.nextMonth}
              onClick={() => period?.nextMonth && setMonth(period.nextMonth)}
              className="p-1.5 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
          <ArrowPathIcon className="h-4 w-4 animate-spin" />
          Loading usage…
        </div>
      )}

      {!loading && error && (
        <div className="mt-4 flex items-center justify-between gap-3 text-sm text-red-700">
          <span>{error}</span>
          <button
            type="button"
            onClick={load}
            className="px-3 py-1.5 rounded-lg border border-red-200 text-red-700 hover:bg-red-50"
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && data && (
        <div className="mt-4 space-y-3">
          <div className="text-2xl font-semibold text-gray-900 tabular-nums">
            {formatUsd(data.totals?.usd)}
          </div>

          <Group title="AI" usd={data.ai?.usd}>
            <MoneyRow
              label="Model spend"
              value={formatUsd(data.ai?.usd)}
              hint={`${formatCount(data.ai?.calls)} calls · ${formatCount(data.ai?.tokensInput)} in / ${formatCount(data.ai?.tokensOutput)} out`}
            />
            {(data.ai?.byVendor || []).map((row) => (
              <div key={`${row.vendor}-${row.model}`} className="text-xs text-gray-500 pl-0.5">
                {row.vendor} {row.model} · {formatCount(row.calls)} · {formatUsd(row.usd)}
              </div>
            ))}
            {data.ai?.auditTurns > 0 && (
              <div className="text-xs text-gray-500">
                {formatCount(data.ai.auditTurns)} Design Studio turns stored on this account with no website
              </div>
            )}
          </Group>

          <Group title="Twilio" usd={data.twilio?.usd}>
            <MoneyRow
              label="Number"
              value={formatUsd(data.twilio?.number?.usd)}
              hint={data.twilio?.number?.count
                ? `${formatCount(data.twilio.number.count)} rented`
                : 'No rented number in this view'}
            />
            {(data.twilio?.number?.lines || []).map((line) => (
              <div key={`${line.phone}-${line.rateKey}`} className="text-xs text-gray-500">
                {line.phone || 'Number'} · {RATE_LABELS[line.rateKey] || line.rateKey} · {line.months} × {formatUsd(line.monthlyUsd)}
              </div>
            ))}
            <MoneyRow
              label="WhatsApp"
              value={formatUsd(data.twilio?.whatsapp?.usd)}
              hint={`${formatCount(data.twilio?.whatsapp?.inbound)} inbound · ${formatCount(data.twilio?.whatsapp?.service)} service (${formatCount(data.twilio?.whatsapp?.serviceFree)} Meta-free) · ${formatCount(data.twilio?.whatsapp?.templates?.marketing)} marketing · ${formatCount(data.twilio?.whatsapp?.templates?.utility)} utility · ${formatCount(data.twilio?.whatsapp?.templates?.authentication)} authentication`}
            />
            <MoneyRow
              label="SMS"
              value={formatUsd(data.twilio?.sms?.usd)}
              hint={`${formatCount(data.twilio?.sms?.count)} messages`}
            />
            <MoneyRow
              label="Voice"
              value={formatUsd(data.twilio?.voice?.usd)}
              hint={`${formatCount(data.twilio?.voice?.inboundMinutes)} inbound min · ${formatCount(data.twilio?.voice?.ilMobileMinutes)} IL mobile min · ${formatCount(data.twilio?.voice?.ilLandlineMinutes)} IL landline min · ${formatCount(data.twilio?.voice?.otherMinutes)} other min`}
            />
          </Group>

          <Group title="Email" usd={data.email?.usd}>
            <MoneyRow
              label="Sends"
              value={formatUsd(data.email?.usd)}
              hint={`${formatCount(data.email?.newsletter)} newsletter · ${formatCount(data.email?.notifications)} customer notifications`}
            />
          </Group>

          <details className="text-sm text-gray-600">
            <summary className="cursor-pointer font-medium text-gray-700">
              How this estimate is priced
            </summary>
            <ul className="mt-2 list-disc pl-5 space-y-1 text-xs text-gray-500">
              {(data.notes || []).map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          </details>
        </div>
      )}
    </div>
  );
};

export default BundleCostEstimate;
